import axios from 'axios';
import config from '../config';

const API_URL = config.API_URL;

// Create axios instance with base URL
const api = axios.create({
  baseURL: API_URL,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Add interceptor to include the auth token in requests
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor for better error handling
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    // Enhanced error handling
    if (!navigator.onLine) {
      error.code = 'NETWORK_ERROR';
      error.message = 'No internet connection. Please check your network and try again.';
    } else if (!error.response) {
      // Network error (no response from server)
      error.code = 'NETWORK_ERROR';
      error.message = 'Network error occurred. Please check your connection and try again.';
    } else if (error.response.status >= 500) {
      // Server error
      error.message = 'Something went wrong on our servers. Please try again in a moment.';
    } else if (error.response.status === 413) {
      // File too large
      error.message = 'File is too large. Please choose a smaller file (under 5MB).';
    } else if (error.response.status === 415) {
      // Unsupported media type
      error.message = 'Invalid file format. Please choose a supported file type.';
    }
    
    return Promise.reject(error);
  }
);

// Group services
const groupService = {
  // Check if group name is available
  checkNameAvailability: async (name) => {
    try {
      const response = await axios.get(`${API_URL}/groups/check-name`, {
        params: { name },
        withCredentials: true
      });
      return response.data;
    } catch (error) {
      console.error('Error checking name availability:', error);
      throw error;
    }
  },

  // Create a new group
  createGroup: async (groupData) => {
    try {
      console.log('Creating group with data:', { 
        name: groupData.name, 
        description: groupData.description?.substring(0, 20) + '...',
        adminName: groupData.adminName,
        hasLogo: !!groupData.logoFile
      });
      
      const token = localStorage.getItem('token');
      console.log('Auth token exists:', !!token);
      
      const response = await api.post('/groups', groupData);
      console.log('Group created successfully:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error creating group:', error);
      if (error.response) {
        console.error('Error response data:', error.response.data);
        console.error('Error response status:', error.response.status);
      } else if (error.request) {
        console.error('No response received from server');
      } else {
        console.error('Error message:', error.message);
      }
      throw error;
    }
  },

  // Get admin's groups
  getAdminGroups: async () => {
    try {
      // Add cache-busting parameters to ensure fresh data
      const cacheBuster = `_t=${Date.now()}&_r=${Math.random()}`;
      console.log('🔒 SECURITY: Fetching fresh admin groups with cache-busting');
      
      const response = await api.get(`/groups?${cacheBuster}`);
      
      console.log('🔒 SECURITY: Admin groups API response count:', response.data?.data?.length || 0);
      return response.data;
    } catch (error) {
      console.error('Error getting admin groups:', error);
      throw error;
    }
  },

  // Get group by ID
  getGroup: async (id) => {
    try {
      const response = await api.get(`/groups/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error getting group:', error);
      throw error;
    }
  },

  // Get public group data for invitations (no auth required)
  getPublicGroup: async (id) => {
    try {
      const response = await api.get(`/groups/${id}/public`);
      return response.data;
    } catch (error) {
      console.error('Error getting public group:', error);
      throw error;
    }
  },

  // Update group
  updateGroup: async (id, groupData) => {
    try {
      // Check if we're dealing with FormData
      const isFormData = groupData instanceof FormData;
      
      // Configure request based on data type
      const config = {};
      
      if (isFormData) {
        // Remove the default content-type so browser sets it with the boundary
        config.headers = {
          'Content-Type': undefined
        };
        
        // Log what data we're sending for debugging
        console.log('Sending FormData with keys:', [...groupData.keys()].join(', '));
      }
      
      // Make the API call
      const response = await api.put(`/groups/${id}`, groupData, config);
      console.log('Group update response:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error updating group:', error);
      if (error.response) {
        console.error('Server error details:', {
          status: error.response.status,
          data: error.response.data
        });
      }
      throw error;
    }
  },

  // Delete group
  deleteGroup: async (id) => {
    try {
      const response = await api.delete(`/groups/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting group:', error);
      throw error;
    }
  },

  // Search for groups
  searchGroups: async (query) => {
    try {
      const response = await api.get('/groups/search', {
        params: { query }
      });
      return response.data;
    } catch (error) {
      console.error('Error searching groups:', error);
      throw error;
    }
  },
  
  // Get user's joined groups
  getUserGroups: async () => {
    try {
      // Add cache-busting parameters to ensure fresh data
      const cacheBuster = `_t=${Date.now()}&_r=${Math.random()}`;
      console.log('🔒 SECURITY: Fetching fresh user groups with cache-busting');
      
      // Debug the token being sent
      const token = localStorage.getItem('token');
      console.log('🔒 SECURITY: Token being sent:', token ? 'EXISTS (length: ' + token.length + ')' : 'MISSING');
      if (token) {
        try {
          const decoded = JSON.parse(atob(token.split('.')[1]));
          console.log('🔒 SECURITY: Decoded token payload:', {
            sub: decoded.sub,
            email: decoded.email,
            phone: decoded.phone,
            exp: new Date(decoded.exp * 1000),
            iss: decoded.iss
          });
        } catch (e) {
          console.log('🔒 SECURITY: Could not decode token:', e.message);
        }
      }
      
      const response = await api.get(`/groups/joined?${cacheBuster}`);
      
      console.log('🔒 SECURITY: User groups API response count:', response.data?.data?.length || 0);
      console.log('🔒 SECURITY: Full API response:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error getting user groups:', error);
      throw error;
    }
  },
  
  // Join a group (creates a join request)
  joinGroup: async (groupId, message = null) => {
    try {
      const response = await api.post(`/groups/${groupId}/join`, { message });
      return response.data;
    } catch (error) {
      console.error('Error creating join request:', error);
      throw error;
    }
  },

  // Leave a group
  leaveGroup: async (groupId) => {
    try {
      const response = await api.post(`/groups/${groupId}/leave`);
      return response.data;
    } catch (error) {
      console.error('Error leaving group:', error);
      throw error;
    }
  },

  // ===== Event Methods =====
  
  // Get group events
  getGroupEvents: async (groupId) => {
    try {
      // Add cache-busting parameter to ensure fresh data
      const response = await api.get(`/groups/${groupId}/events?_t=${Date.now()}`);
      return response.data;
    } catch (error) {
      console.error('Error getting group events:', error);
      throw error;
    }
  },
  
  // Get unique event categories for a group
  getEventCategories: async (groupId) => {
    try {
      const response = await api.get(`/groups/${groupId}/events/categories`);
      return response.data;
    } catch (error) {
      console.error('Error getting event categories:', error);
      throw error;
    }
  },
  
  // Create an event
  createEvent: async (groupId, eventData) => {
    try {
      console.log('Creating event with data:', eventData);
      
      const response = await api.post(`/groups/${groupId}/events`, eventData, {
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      return response.data;
    } catch (error) {
      console.error('Error creating event:', error);
      throw error;
    }
  },
  
  // Update an event
  updateEvent: async (groupId, eventId, eventData) => {
    try {
      console.log('Updating event with data:', eventData);
      
      const response = await api.put(`/groups/${groupId}/events/${eventId}`, eventData, {
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      return response.data;
    } catch (error) {
      console.error('Error updating event:', error);
      throw error;
    }
  },
  
  // Delete an event
  deleteEvent: async (groupId, eventId) => {
    try {
      const response = await api.delete(`/groups/${groupId}/events/${eventId}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting event:', error);
      throw error;
    }
  },

  // Get a single event by ID
  getEvent: async (groupId, eventId) => {
    try {
      console.log(`Fetching event details - Group ID: ${groupId}, Event ID: ${eventId}`);
      // Add cache-busting parameter to ensure fresh data
      const response = await api.get(`/groups/${groupId}/events/${eventId}?_t=${Date.now()}`);
      console.log('Event details response:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error getting event details:', error);
      console.error('Request URL:', `/groups/${groupId}/events/${eventId}`);
      if (error.response) {
        console.error('Response status:', error.response.status);
        console.error('Response data:', error.response.data);
      }
      throw error;
    }
  },
  
  // Sponsor an event
  sponsorEvent: async (groupId, eventId, sponsorData) => {
    try {
      const response = await api.post(`/groups/${groupId}/events/${eventId}/sponsor`, sponsorData);
      return response.data;
    } catch (error) {
      console.error('Error sponsoring event:', error);
      throw error;
    }
  },
  
  // Donate to a group (general donation)
  donateToGroup: async (groupId, donationData) => {
    try {
      const response = await api.post(`/groups/${groupId}/donate`, donationData);
      return response.data;
    } catch (error) {
      console.error('Error donating to group:', error);
      throw error;
    }
  },
  
  // Get event sponsors with dedications
  getEventSponsors: async (groupId, eventId) => {
    try {
      const response = await api.get(`/groups/${groupId}/events/${eventId}/sponsors`);
      return response.data;
    } catch (error) {
      console.error('Error getting event sponsors:', error);
      throw error;
    }
  },

  // RSVP to an event
  rsvpEvent: async (groupId, eventId, data) => {
    try {
      console.log(`🔄 [CACHE FIX] RSVPing to event ${eventId} in group ${groupId} with data:`, data);
      
      const response = await api.post(`/groups/${groupId}/events/${eventId}/rsvp?_t=${Date.now()}`, data);
      console.log('🔄 [CACHE FIX] RSVP response:', response.data);
      
      // Force a small delay to ensure database write completes
      await new Promise(resolve => setTimeout(resolve, 100));
      
      return response.data;
    } catch (error) {
      console.error('❌ [CACHE FIX] Error RSVPing to event:', error);
      if (error.response) {
        console.error('❌ [CACHE FIX] Server error details:', {
          status: error.response.status,
          data: error.response.data,
          headers: error.response.headers
        });
      }
      throw error;
    }
  },

  // Delete an RSVP
  deleteEventRsvp: async (groupId, eventId) => {
    try {
      console.log(`🔄 [CACHE FIX] Deleting RSVP for event ${eventId} in group ${groupId}`);
      
      const response = await api.delete(`/groups/${groupId}/events/${eventId}/rsvp?_t=${Date.now()}`);
      console.log('🔄 [CACHE FIX] Delete RSVP response:', response.data);
      
      // Force a small delay to ensure database write completes
      await new Promise(resolve => setTimeout(resolve, 100));
      
      return response.data;
    } catch (error) {
      console.error('❌ [CACHE FIX] Error deleting event RSVP:', error);
      if (error.response) {
        console.error('❌ [CACHE FIX] Server error details:', {
          status: error.response.status,
          data: error.response.data,
          headers: error.response.headers
        });
      }
      throw error;
    }
  },

  // Get all RSVPs for an event
  getEventRsvps: async (groupId, eventId) => {
    try {
      const response = await api.get(`/groups/${groupId}/events/${eventId}/rsvps?_t=${Date.now()}&_r=${Math.random()}`);
      return response.data;
    } catch (error) {
      console.error('Error getting event RSVPs:', error);
      throw error;
    }
  },

  // Get user's RSVP for an event
  getUserEventRsvp: async (groupId, eventId) => {
    try {
      const response = await api.get(`/groups/${groupId}/events/${eventId}/rsvp/me?_t=${Date.now()}&_r=${Math.random()}`);
      return response.data;
    } catch (error) {
      console.error('Error getting user event RSVP:', error);
      throw error;
    }
  },
  
  // ===== Saved Events Methods =====
  
  // Get all saved events (events user has RSVP'd to)
  getSavedEvents: async () => {
    try {
      const response = await api.get('/events/saved');
      return response.data;
    } catch (error) {
      console.error('Error getting saved events:', error);
      throw error;
    }
  },
  
  // ===== Class Methods =====
  
  // Get group classes
  getGroupClasses: async (groupId) => {
    try {
      console.log(`Fetching classes for group ${groupId}`);
      // Add cache-busting parameter to ensure fresh data
      const response = await api.get(`/groups/${groupId}/classes?_t=${Date.now()}`);
      
      console.log('Class API response:', response);
      
      // Handle different response structures
      if (response.data && response.data.data && Array.isArray(response.data.data)) {
        // If the API returns { success: true, data: [...] }
        console.log(`Received ${response.data.data.length} classes in data.data array`);
        return response.data.data;
      } else if (response.data && Array.isArray(response.data)) {
        // If the API returns the array directly
        console.log(`Received ${response.data.length} classes in data array`);
        return response.data;
      } else if (response.data) {
        console.error('Unexpected data structure for classes:', response.data);
        // Return an empty array as fallback
        return [];
      }
      
      // If we get here, response.data is likely null or undefined
      console.error('No data in classes response:', response);
      return [];
    } catch (error) {
      console.error('Error getting group classes:', error);
      if (error.response) {
        console.error('Error response status:', error.response.status);
        console.error('Error response data:', error.response.data);
      } else if (error.request) {
        console.error('No response received');
      } else {
        console.error('Error message:', error.message);
      }
      // Return an empty array instead of throwing to prevent component crashes
      return [];
    }
  },
  
  // Get unique class categories for a group
  getClassCategories: async (groupId) => {
    try {
      const response = await api.get(`/groups/${groupId}/classes/categories`);
      return response.data;
    } catch (error) {
      console.error('Error getting class categories:', error);
      throw error;
    }
  },
  
  // Create a class
  createClass: async (groupId, classData) => {
    try {
      console.log('Creating class with data:', classData);
      
      const response = await api.post(`/groups/${groupId}/classes`, classData, {
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      return response.data;
    } catch (error) {
      console.error('Error creating class:', error);
      throw error;
    }
  },
  
  // Update a class
  updateClass: async (groupId, classId, classData) => {
    try {
      console.log('Updating class with data:', classData);
      
      // Add cache-busting to ensure fresh data after update
      const response = await api.put(`/groups/${groupId}/classes/${classId}?_t=${Date.now()}`, classData, {
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      // Force a small delay to ensure database write completes
      await new Promise(resolve => setTimeout(resolve, 100));
      
      return response.data;
    } catch (error) {
      console.error('Error updating class:', error);
      throw error;
    }
  },
  
  // Delete a class
  deleteClass: async (groupId, classId) => {
    try {
      console.log(`Deleting class - Group ID: ${groupId}, Class ID: ${classId}`);
      
      // Validate input parameters
      if (!groupId || !classId) {
        console.error('Missing required parameters for deleteClass:', { groupId, classId });
        throw new Error('Missing groupId or classId for class deletion');
      }
      
      // Log the exact URL being called
      const url = `/groups/${groupId}/classes/${classId}`;
      console.log('DELETE request URL:', url);
      
      const response = await api.delete(url);
      console.log('Delete class API response:', response);
      
      return response.data;
    } catch (error) {
      console.error('Error deleting class:', error);
      if (error.response) {
        console.error('Error response status:', error.response.status);
        console.error('Error response data:', error.response.data);
      } else if (error.request) {
        console.error('No response received for delete request');
      }
      throw error;
    }
  },

  // Get a single class by ID
  getClass: async (groupId, classId) => {
    try {
      console.log(`Fetching class details - Group ID: ${groupId}, Class ID: ${classId}`);
      // Add cache-busting parameter to ensure fresh data
      const response = await api.get(`/groups/${groupId}/classes/${classId}?_t=${Date.now()}`);
      console.log('Class details response:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error getting class details:', error);
      console.error('Request URL:', `/groups/${groupId}/classes/${classId}`);
      if (error.response) {
        console.error('Response status:', error.response.status);
        console.error('Response data:', error.response.data);
      }
      throw error;
    }
  },
  
  // Enroll in a class
  enrollClass: async (groupId, classId, data) => {
    try {
      console.log(`🔄 [CACHE FIX] Enrolling in class ${classId} in group ${groupId} with data:`, data);
      
      const response = await api.post(`/groups/${groupId}/classes/${classId}/enroll?_t=${Date.now()}`, data);
      console.log('🔄 [CACHE FIX] Enrollment response:', response.data);
      
      // Force a small delay to ensure database write completes
      await new Promise(resolve => setTimeout(resolve, 100));
      
      return response.data;
    } catch (error) {
      console.error('❌ [CACHE FIX] Error enrolling in class:', error);
      if (error.response) {
        console.error('❌ [CACHE FIX] Server error details:', {
          status: error.response.status,
          data: error.response.data,
          headers: error.response.headers
        });
      }
      throw error;
    }
  },

  // Delete a class enrollment
  deleteClassEnrollment: async (groupId, classId) => {
    try {
      console.log(`🔄 [CACHE FIX] Deleting enrollment for class ${classId} in group ${groupId}`);
      
      const response = await api.delete(`/groups/${groupId}/classes/${classId}/enroll?_t=${Date.now()}`);
      console.log('🔄 [CACHE FIX] Delete enrollment response:', response.data);
      
      // Force a small delay to ensure database write completes
      await new Promise(resolve => setTimeout(resolve, 100));
      
      return response.data;
    } catch (error) {
      console.error('❌ [CACHE FIX] Error deleting class enrollment:', error);
      if (error.response) {
        console.error('❌ [CACHE FIX] Server error details:', {
          status: error.response.status,
          data: error.response.data,
          headers: error.response.headers
        });
      }
      throw error;
    }
  },

  // Get all enrollments for a class
  getClassEnrollments: async (groupId, classId) => {
    try {
      console.log(`🔄 [CACHE FIX] Getting enrollments for class ${classId} in group ${groupId}`);
      
      const response = await api.get(`/groups/${groupId}/classes/${classId}/enrollments?_t=${Date.now()}&_r=${Math.random()}`);
      console.log('🔄 [CACHE FIX] Class enrollments response:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ [CACHE FIX] Error getting class enrollments:', error);
      throw error;
    }
  },

  // Get user's enrollment for a class
  getUserClassEnrollment: async (groupId, classId) => {
    try {
      console.log(`🔄 [CACHE FIX] Getting current user enrollment for class ${classId} in group ${groupId}`);
      
      const response = await api.get(`/groups/${groupId}/classes/${classId}/enroll/me?_t=${Date.now()}&_r=${Math.random()}`);
      console.log('🔄 [CACHE FIX] User class enrollment response:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ [CACHE FIX] Error getting user class enrollment:', error);
      throw error;
    }
  },

  // ===== Class Sponsor Methods =====
  
  // Sponsor a class
  sponsorClass: async (groupId, classId, sponsorData) => {
    try {
      console.log(`Sponsoring class ${classId} in group ${groupId}`, sponsorData);
      const response = await api.post(`/groups/${groupId}/classes/${classId}/sponsor`, sponsorData);
      return response.data;
    } catch (error) {
      console.error('Error sponsoring class:', error);
      throw error;
    }
  },
  
  // Get class sponsors with dedications
  getClassSponsors: async (groupId, classId) => {
    try {
      const response = await api.get(`/groups/${groupId}/classes/${classId}/sponsors`);
      return response.data;
    } catch (error) {
      console.error('Error getting class sponsors:', error);
      throw error;
    }
  },
  
  // ===== Membership Methods =====
  
  // Get group memberships
  getGroupMemberships: async (groupId) => {
    try {
      console.log(`Fetching memberships for group ${groupId}`);
      const response = await api.get(`/groups/${groupId}/memberships`);
      
      console.log('Membership API response:', response);
      
      // Handle different response structures
      if (response.data && response.data.data && Array.isArray(response.data.data)) {
        // If the API returns { success: true, data: [...] }
        console.log(`Received ${response.data.data.length} memberships in data.data array`);
        return response.data.data;
      } else if (response.data && Array.isArray(response.data)) {
        // If the API returns the array directly
        console.log(`Received ${response.data.length} memberships in data array`);
        return response.data;
      } else if (response.data && typeof response.data === 'object') {
        console.log('Received object response for memberships, checking format');
        // For cases when response is { success: true, count: x, data: [...] }
        if (response.data.success && Array.isArray(response.data.data)) {
          console.log(`Received ${response.data.data.length} memberships in success.data format`);
          return response.data.data;
        }
        console.error('Unexpected data structure for memberships:', response.data);
        // Return an empty array as fallback
        return [];
      }
      
      // If we get here, response.data is likely null or undefined
      console.error('No data in memberships response:', response);
      return [];
    } catch (error) {
      console.error('Error getting group memberships:', error);
      if (error.response) {
        console.error('Error response status:', error.response.status);
        console.error('Error response data:', error.response.data);
      } else if (error.request) {
        console.error('No response received');
      } else {
        console.error('Error message:', error.message);
      }
      // Return an empty array instead of throwing to prevent component crashes
      return [];
    }
  },
  
  // Create a membership
  createMembership: async (groupId, membershipData) => {
    try {
      const response = await api.post(`/groups/${groupId}/memberships`, membershipData);
      return response.data;
    } catch (error) {
      console.error('Error creating membership:', error);
      throw error;
    }
  },
  
  // Update a membership
  updateMembership: async (groupId, membershipId, membershipData) => {
    try {
      const response = await api.put(`/groups/${groupId}/memberships/${membershipId}`, membershipData);
      return response.data;
    } catch (error) {
      console.error('Error updating membership:', error);
      throw error;
    }
  },
  
  // Delete a membership
  deleteMembership: async (groupId, membershipId) => {
    try {
      const response = await api.delete(`/groups/${groupId}/memberships/${membershipId}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting membership:', error);
      throw error;
    }
  },
  
  // Subscribe to a membership
  subscribeMembership: async (groupId, membershipId, subscriptionData = {}) => {
    try {
      const response = await api.post(`/groups/${groupId}/memberships/${membershipId}/subscribe`, subscriptionData);
      return response.data;
    } catch (error) {
      console.error('Error subscribing to membership:', error);
      throw error;
    }
  },
  
  // ===== Admin Member Management =====
  
  // Get all approved members of a group
  getGroupMembers: async (groupId) => {
    try {
      console.log('API: Making request to get group members for groupId:', groupId);
      const response = await api.get(`/groups/${groupId}/members`);
      console.log('API: Raw response received:', response);
      console.log('API: Response data:', response.data);
      console.log('API: Response data structure check:', {
        hasData: !!response.data,
        hasDataArray: !!(response.data && response.data.data),
        isDataArray: Array.isArray(response.data.data),
        dataLength: response.data.data ? response.data.data.length : 0
      });
      
      // Log each member's structure
      if (response.data && response.data.data && Array.isArray(response.data.data)) {
        console.log('API: Members data structure:');
        response.data.data.forEach((member, i) => {
          console.log(`API: Member ${i + 1}:`, {
            user_id: member.user_id,
            hasUser: !!member.user,
            userName: member.user?.name || 'NO NAME',
            userEmail: member.user?.email || 'NO EMAIL',
            userKeys: member.user ? Object.keys(member.user) : 'NO USER OBJECT'
          });
        });
      }
      
      return response.data;
    } catch (error) {
      console.error('Error getting group members:', error);
      throw error;
    }
  },
  
  // Get all approved members of a group with full user details
  getGroupMembersWithDetails: async (groupId) => {
    try {
      console.log('API: Making request to get group members with details for groupId:', groupId);
      // Add cache-busting parameter to ensure fresh data
      const cacheBuster = `_t=${Date.now()}&_r=${Math.random()}`;
      const response = await api.get(`/groups/${groupId}/members-with-details?${cacheBuster}`);
      console.log('API: Members with details response:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error getting group members with details:', error);
      throw error;
    }
  },

  // Get member profile with all related data (RSVPs, donations, pledges, ticket purchases)
  getMemberProfile: async (groupId, userId) => {
    try {
      const url = `/groups/${groupId}/members/${userId}/profile`;
      console.log('API: Making request to get member profile for userId:', userId, 'in groupId:', groupId);
      console.log('API: Full request URL:', `${API_URL}${url}`);
      console.log('API: BaseURL from axios:', api.defaults.baseURL);
      const response = await api.get(url);
      console.log('API: Member profile response:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error getting member profile:', error);
      console.error('Error response:', error.response);
      throw error;
    }
  },
  
  // Get all pending join requests for a group (alias for backward compatibility)
  getPendingRequests: async (groupId) => {
    try {
      // Add cache-busting parameter to ensure fresh data
      const cacheBuster = `_t=${Date.now()}&_r=${Math.random()}`;
      const response = await api.get(`/groups/${groupId}/requests?${cacheBuster}`);
      return response.data?.data || [];
    } catch (error) {
      console.error('Error getting pending requests:', error);
      throw error;
    }
  },

  // Get all pending join requests for a group (new method name)
  getPendingJoinRequests: async (groupId) => {
    try {
      // Add cache-busting parameter to ensure fresh data
      const cacheBuster = `_t=${Date.now()}&_r=${Math.random()}`;
      const response = await api.get(`/groups/${groupId}/requests?${cacheBuster}`);
      return response.data?.data || [];
    } catch (error) {
      console.error('Error getting pending join requests:', error);
      throw error;
    }
  },
  
  // Approve a join request (legacy method - keeping for backward compatibility)
  approveRequest: async (groupId, userId) => {
    try {
      const response = await api.post(`/groups/${groupId}/requests/${userId}/approve`);
      return response.data;
    } catch (error) {
      console.error('Error approving request:', error);
      throw error;
    }
  },

  // Approve a join request by request ID
  approveJoinRequest: async (groupId, requestId, adminNotes = null) => {
    try {
      const response = await api.post(`/groups/${groupId}/requests/${requestId}/approve`, { 
        adminNotes 
      });
      return response.data;
    } catch (error) {
      console.error('Error approving join request:', error);
      throw error;
    }
  },
  
  // Deny a join request (legacy method - keeping for backward compatibility)
  denyRequest: async (groupId, userId) => {
    try {
      const response = await api.post(`/groups/${groupId}/requests/${userId}/deny`);
      return response.data;
    } catch (error) {
      console.error('Error denying request:', error);
      throw error;
    }
  },

  // Deny a join request by request ID
  denyJoinRequest: async (groupId, requestId, adminNotes = null) => {
    try {
      const response = await api.post(`/groups/${groupId}/requests/${requestId}/deny`, { 
        adminNotes 
      });
      return response.data;
    } catch (error) {
      console.error('Error denying join request:', error);
      throw error;
    }
  },
  
  // Bulk approve all pending requests
  approveAllRequests: async (groupId) => {
    try {
      const response = await api.post(`/groups/${groupId}/requests/approve-all`);
      return response.data;
    } catch (error) {
      console.error('Error approving all requests:', error);
      throw error;
    }
  },

  // Alias for bulk approve
  approveAllJoinRequests: async (groupId) => {
    try {
      const response = await api.post(`/groups/${groupId}/requests/approve-all`);
      return response.data;
    } catch (error) {
      console.error('Error approving all join requests:', error);
      throw error;
    }
  },
  
  // Remove a member from the group
  removeMember: async (groupId, userId) => {
    try {
      const response = await api.delete(`/groups/${groupId}/members/${userId}`);
      return response.data;
    } catch (error) {
      console.error('Error removing member:', error);
      throw error;
    }
  },

  // Reactivate a blocked member
  activateMember: async (groupId, userId) => {
    try {
      const response = await api.post(`/groups/${groupId}/members/${userId}/activate`);
      return response.data;
    } catch (error) {
      console.error('Error activating member:', error);
      throw error;
    }
  },

  // Get batch users by IDs
  getBatchUsers: async (userIds) => {
    try {
      if (!userIds || !Array.isArray(userIds) || userIds.length === 0) {
        return { success: true, data: [] };
      }
      
      const response = await api.get(`/users/batch?ids=${userIds.join(',')}`);
      return response.data;
    } catch (error) {
      console.error('Error getting batch users:', error);
      throw error;
    }
  },

  // Community Posts API functions
  getCommunityPosts: async (groupId) => {
    try {
      // Add cache-busting parameter to ensure fresh data
      const timestamp = Date.now();
      const response = await api.get(`/community/posts/${groupId}?_t=${timestamp}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching community posts:', error);
      throw error;
    }
  },

  createCommunityPost: async (groupId, postData) => {
    try {
      // Community posts now use JSON with base64 images (like events)
      const response = await api.post(`/community/posts/${groupId}`, postData, {
        headers: {
          'Content-Type': 'application/json'
        }
      });
      return response.data;
    } catch (error) {
      console.error('❌ Error creating community post:', error);
      if (error.response) {
        console.error('Response error:', error.response.data);
      }
      throw error;
    }
  },

  updateCommunityPost: async (postId, postData) => {
    try {
      // Community posts now use JSON with base64 images (like events)
      const response = await api.put(`/community/posts/${postId}`, postData, {
        headers: {
          'Content-Type': 'application/json'
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error updating community post:', error);
      throw error;
    }
  },

  deleteCommunityPost: async (postId) => {
    try {
      const response = await api.delete(`/community/posts/${postId}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting community post:', error);
      throw error;
    }
  },

  likeCommunityPost: async (postId) => {
    try {
      const response = await api.post(`/community/posts/post/${postId}/like`);
      return response.data;
    } catch (error) {
      console.error('Error liking community post:', error);
      throw error;
    }
  },

  unlikeCommunityPost: async (postId) => {
    try {
      const response = await api.delete(`/community/posts/post/${postId}/like`);
      return response.data;
    } catch (error) {
      console.error('Error unliking community post:', error);
      throw error;
    }
  },

  // Donation Product Types
  getDonationProductTypes: async (groupId) => {
    try {
      const response = await api.get(`/groups/${groupId}/donation-product-types`);
      return response.data;
    } catch (error) {
      console.error('Error fetching donation product types:', error);
      throw error;
    }
  },

  createDonationProductType: async (groupId, productTypeData) => {
    try {
      const response = await api.post(`/groups/${groupId}/donation-product-types`, productTypeData);
      return response.data;
    } catch (error) {
      console.error('Error creating donation product type:', error);
      throw error;
    }
  },

  updateDonationProductType: async (groupId, productTypeId, productTypeData) => {
    try {
      const response = await api.put(`/groups/${groupId}/donation-product-types/${productTypeId}`, productTypeData);
      return response.data;
    } catch (error) {
      console.error('Error updating donation product type:', error);
      throw error;
    }
  },

  deleteDonationProductType: async (groupId, productTypeId) => {
    try {
      const response = await api.delete(`/groups/${groupId}/donation-product-types/${productTypeId}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting donation product type:', error);
      throw error;
    }
  },

  // Community Post Tags
  getPostTags: async (groupId) => {
    try {
      const response = await api.get(`/groups/${groupId}/post-tags`);
      return response.data;
    } catch (error) {
      console.error('Error fetching post tags:', error);
      throw error;
    }
  },

  createPostTag: async (groupId, tagData) => {
    try {
      const response = await api.post(`/groups/${groupId}/post-tags`, tagData);
      return response.data;
    } catch (error) {
      console.error('Error creating post tag:', error);
      throw error;
    }
  },

  updatePostTag: async (groupId, tagId, tagData) => {
    try {
      const response = await api.put(`/groups/${groupId}/post-tags/${tagId}`, tagData);
      return response.data;
    } catch (error) {
      console.error('Error updating post tag:', error);
      throw error;
    }
  },

  deletePostTag: async (groupId, tagId) => {
    try {
      const response = await api.delete(`/groups/${groupId}/post-tags/${tagId}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting post tag:', error);
      throw error;
    }
  },

  // Group Donations
  getGroupDonations: async (groupId, params = {}) => {
    try {
      const response = await api.get(`/groups/${groupId}/donations`, { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching group donations:', error);
      throw error;
    }
  },

  // Event Ticket Purchases
  getEventTicketPurchases: async (groupId, eventId) => {
    try {
      const response = await api.get(`/groups/${groupId}/events/${eventId}/ticket-purchases`);
      return response.data;
    } catch (error) {
      console.error('Error fetching event ticket purchases:', error);
      throw error;
    }
  },

  // Group Ticket Purchases (all tickets for the group)
  getGroupTicketPurchases: async (groupId, params = {}) => {
    try {
      const response = await api.get(`/groups/${groupId}/ticket-purchases`, { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching group ticket purchases:', error);
      throw error;
    }
  },

  // ===== Prayer/Agenda Methods =====
  
  // Get group prayer times / agenda items
  getGroupPrayer: async (groupId) => {
    try {
      console.log(`Fetching prayer times/agenda for group ${groupId}`);
      // Add cache-busting parameter to ensure fresh data
      const response = await api.get(`/groups/${groupId}/agenda/public?_t=${Date.now()}`);
      console.log('Prayer/agenda API response:', response);
      
      // Handle different response structures
      if (response.data && response.data.data) {
        const data = response.data.data;
        
        // Check if data is an array
        if (Array.isArray(data)) {
          console.log(`Received ${data.length} prayer items in data.data array`);
          return data;
        }
        
        // Check if data is an object with day keys (grouped by day)
        if (typeof data === 'object' && data !== null) {
          console.log('Data is grouped by day, flattening...');
          const allItems = [];
          const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Shabbat'];
          
          // Flatten all day arrays into a single array
          days.forEach(day => {
            if (data[day] && Array.isArray(data[day])) {
              console.log(`Adding ${data[day].length} items from ${day}`);
              allItems.push(...data[day]);
            }
          });
          
          console.log(`Flattened ${allItems.length} total prayer items`);
          return allItems;
        }
        
        console.error('Unexpected data structure for prayer times:', data);
        return [];
      } else if (response.data && Array.isArray(response.data)) {
        // If the API returns the array directly
        console.log(`Received ${response.data.length} prayer items in data array`);
        return response.data;
      } else if (response.data) {
        console.error('Unexpected data structure for prayer times:', response.data);
        return [];
      }
      
      // If we get here, response.data is likely null or undefined
      console.error('No data in prayer times response:', response);
      return [];
    } catch (error) {
      console.error('Error getting group prayer times:', error);
      if (error.response) {
        console.error('Error response status:', error.response.status);
        console.error('Error response data:', error.response.data);
      } else if (error.request) {
        console.error('No response received');
      } else {
        console.error('Error message:', error.message);
      }
      // Return an empty array instead of throwing to prevent component crashes
      return [];
    }
  },

  // ============================================
  // PLEDGES API METHODS
  // ============================================

  // Get all pledge types for a group
  getPledgeTypes: async (groupId) => {
    try {
      console.log('API: Getting pledge types for group:', groupId);
      const response = await api.get(`/groups/${groupId}/pledge-types`);
      console.log('API: Pledge types response:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error getting pledge types:', error);
      throw error;
    }
  },

  // Create a new pledge type
  createPledgeType: async (groupId, pledgeTypeData) => {
    try {
      console.log('API: Creating pledge type for group:', groupId, pledgeTypeData);
      const response = await api.post(`/groups/${groupId}/pledge-types`, pledgeTypeData);
      console.log('API: Create pledge type response:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error creating pledge type:', error);
      throw error;
    }
  },

  // Update a pledge type
  updatePledgeType: async (groupId, pledgeTypeId, pledgeTypeData) => {
    try {
      console.log('API: Updating pledge type:', groupId, pledgeTypeId, pledgeTypeData);
      const response = await api.put(`/groups/${groupId}/pledge-types/${pledgeTypeId}`, pledgeTypeData);
      console.log('API: Update pledge type response:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error updating pledge type:', error);
      throw error;
    }
  },

  // Delete a pledge type
  deletePledgeType: async (groupId, pledgeTypeId) => {
    try {
      console.log('API: Deleting pledge type:', groupId, pledgeTypeId);
      const response = await api.delete(`/groups/${groupId}/pledge-types/${pledgeTypeId}`);
      console.log('API: Delete pledge type response:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error deleting pledge type:', error);
      throw error;
    }
  },

  // Get all pledges for a group
  getPledges: async (groupId, filters = {}) => {
    try {
      console.log('API: Getting pledges for group:', groupId, 'filters:', filters);
      // Add timestamp to prevent caching
      const paramsObj = { ...filters, _t: Date.now() };
      const params = new URLSearchParams(paramsObj).toString();
      const url = `/groups/${groupId}/pledges${params ? `?${params}` : ''}`;
      const response = await api.get(url, {
        headers: {
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache'
        }
      });
      console.log('API: Pledges response:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error getting pledges:', error);
      throw error;
    }
  },

  // Create a new pledge
  createPledge: async (pledgeData) => {
    try {
      console.log('API: Creating pledge:', pledgeData);
      const response = await api.post(`/groups/${pledgeData.group_id}/pledges`, pledgeData);
      console.log('API: Create pledge response:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error creating pledge:', error);
      throw error;
    }
  },

  // Update a pledge
  updatePledge: async (pledgeId, pledgeData) => {
    try {
      console.log('API: Updating pledge:', pledgeId, pledgeData);
      const response = await api.put(`/pledges/${pledgeId}`, pledgeData);
      console.log('API: Update pledge response:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error updating pledge:', error);
      throw error;
    }
  },

  // Delete a pledge
  deletePledge: async (pledgeId) => {
    try {
      console.log('API: Deleting pledge:', pledgeId);
      const response = await api.delete(`/pledges/${pledgeId}`);
      console.log('API: Delete pledge response:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error deleting pledge:', error);
      throw error;
    }
  },

  // Get unpaid pledges for a member (for native app)
  getUnpaidPledgesForMember: async (memberId, groupId = null) => {
    try {
      console.log('API: Getting unpaid pledges for member:', memberId, 'group:', groupId);
      const params = groupId ? `?group_id=${groupId}` : '';
      const response = await api.get(`/members/${memberId}/unpaid-pledges${params}`);
      console.log('API: Unpaid pledges response:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error getting unpaid pledges:', error);
      throw error;
    }
  },

  // ===== Prayer Names API =====
  
  // Get prayer names for a group
  getPrayerNames: async (groupId, params = {}) => {
    try {
      const response = await api.get(`/groups/${groupId}/prayer-names`, { params });
      return response.data;
    } catch (error) {
      console.error('Error getting prayer names:', error);
      throw error;
    }
  },

  // Create a new prayer name
  createPrayerName: async (data) => {
    try {
      const response = await api.post('/prayer-names', data);
      return response.data;
    } catch (error) {
      console.error('Error creating prayer name:', error);
      throw error;
    }
  },

  // Update a prayer name
  updatePrayerName: async (nameId, data) => {
    try {
      const response = await api.put(`/prayer-names/${nameId}`, data);
      return response.data;
    } catch (error) {
      console.error('Error updating prayer name:', error);
      throw error;
    }
  },

  // Delete a prayer name
  deletePrayerName: async (nameId) => {
    try {
      const response = await api.delete(`/prayer-names/${nameId}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting prayer name:', error);
      throw error;
    }
  },

  // Get pending name requests (not yet added to prayer list)
  getPendingNameRequests: async (groupId) => {
    try {
      const response = await api.get(`/groups/${groupId}/pending-name-requests`);
      return response.data;
    } catch (error) {
      console.error('Error getting pending name requests:', error);
      throw error;
    }
  },

  // Add a name request to prayer list
  addNameRequestToPrayerList: async (requestId, data) => {
    try {
      const response = await api.post(`/name-requests/${requestId}/add-to-prayer-list`, data);
      return response.data;
    } catch (error) {
      console.error('Error adding name request to prayer list:', error);
      throw error;
    }
  },

  // Bulk add multiple name requests to prayer list
  bulkAddNameRequestsToPrayerList: async (data) => {
    try {
      const response = await api.post('/name-requests/bulk-add-to-prayer-list', data);
      return response.data;
    } catch (error) {
      console.error('Error bulk adding name requests:', error);
      throw error;
    }
  },

  // Get name requests for a group
  getNameRequests: async (groupId, params = {}) => {
    try {
      const response = await api.get(`/groups/${groupId}/name-requests`, { params });
      return response.data;
    } catch (error) {
      console.error('Error getting name requests:', error);
      throw error;
    }
  },

  // Accept a name request
  acceptNameRequest: async (requestId, groupId) => {
    try {
      const response = await api.post(`/name-requests/${requestId}/accept`, { 
        prayer_group_id: groupId 
      });
      return response.data;
    } catch (error) {
      console.error('Error accepting name request:', error);
      throw error;
    }
  },

  // Decline a name request
  declineNameRequest: async (requestId, reason = '') => {
    try {
      const response = await api.post(`/name-requests/${requestId}/decline`, { 
        decline_reason: reason 
      });
      return response.data;
    } catch (error) {
      console.error('Error declining name request:', error);
      throw error;
    }
  },

  // Check if group name is available
  checkGroupNameAvailability: async (groupName) => {
    try {
      const response = await api.get('/groups/check-name', { params: { name: groupName } });
      return response.data;
    } catch (error) {
      console.error('Error checking group name:', error);
      throw error;
    }
  },

  // Expose the api instance for direct use
  api: api
};

export default groupService;